import { useEffect, useState } from "react";
import {
  dispatchVariantChangedAnalyticsEvent,
  dispatchPageLoadAnalyticsEvent,
} from "../../utils/analytics/analytics";
import { Variant, Product, PageConfig, FeatureFlags } from "../../types/types";
import { setCustomAttribute } from "../../utils/newrelic/requests";

type Props = {
  product: Product;
  variant: Variant;
  pageConfig: PageConfig;
  shouldFireGoogleEvents?: boolean;
  flags?: FeatureFlags;
  isSelected: boolean;
};

function dispatchVariantChangedEvent(
  type: string,
  selectedVariant: Variant,
  product: Product,
  isSelected: boolean,
) {
  dispatchVariantChangedAnalyticsEvent(
    type,
    selectedVariant,
    product,
    isSelected,
  );

  document.dispatchEvent(
    new CustomEvent("jl-events.browser.productChanged", {
      detail: {
        productCode: selectedVariant.displayCode,
        productName: selectedVariant.title,
        isSkuAvailableOnline:
          selectedVariant.availability?.availableToOrder || "",
        skuId: selectedVariant.aliases.skuId,
        skuPrice: selectedVariant.price?.value || "",
        changeType: type,
      },
    }),
  );
}

export function usePageLevelEvents({
  product,
  variant,
  pageConfig,
  flags,
  isSelected,
}: Props) {
  const [differentiators, setDifferentiators] = useState(
    variant.differentiators,
  );
  // pdp-scaffold-google-analytics-poc

  useEffect(() => {
    if (differentiators?.colour !== variant?.differentiators?.colour) {
      dispatchVariantChangedEvent("colour", variant, product, isSelected);
      setDifferentiators(variant.differentiators);
      return;
    }
    if (differentiators?.size !== variant?.differentiators?.size) {
      dispatchVariantChangedEvent("size", variant, product, isSelected);
      setDifferentiators(variant.differentiators);
      return;
    }

    // These will all only be called if colour and size haven't changed,
    // which should mean only on page load.
    dispatchPageLoadAnalyticsEvent(
      product,
      variant,
      isSelected,
      pageConfig.name,
    );
  }, [product, variant]);

  useEffect(() => {
    setCustomAttribute("pageConfig", `${pageConfig.name}`);
    setCustomAttribute(
      "pageConfigSelectionMethod",
      `${pageConfig.selectionMethod}`,
    );
    setCustomAttribute("variantCount", `${product.variants.length}`);
  }, [pageConfig]);

  useEffect(() => {
    if (flags?.usePrefetching?.value) {
      setCustomAttribute("prefetchingEnabled", "true");
    }
  }, [flags]);
}
